import React, { useState } from 'react';
import { AnalyticsLocationProvider } from 'latitude-analytics';
import styled from 'styled-components';
import { Box, Flex } from '../Box/Box';
import {
  MARGIN,
  BREAKPOINT,
  LOAN_PAY_FREQUENCY,
  PL_APPLY_CTA_TRACK_ID
} from '../../utils/constants';
import CalculatorTotals from './CalculatorTotals';
import CalculatorFields from './CalculatorFields';
import PLDisclaimerContent from './PersonalLoanDisclaimerContent';

const INITIAL_REPAYMENT_FREQ = LOAN_PAY_FREQUENCY.WEEKLY;

const StyledCalculatorFlex = styled(Flex)`
  flex-direction: column;
  @media (min-width: ${BREAKPOINT.LG}) {
    flex-direction: row;
  }
`;

const StyledDisclaimerBox = styled(Box)`
  background-color: transparent;
  margin-top: 0;
  width: 100%;
  margin-top: 24px;

  @media (min-width: ${BREAKPOINT.LG}) {
    padding-top: 0;
    margin-left: 0;
  }
`;

const REPAYMENT_FREQ_INFO = {
  [LOAN_PAY_FREQUENCY.WEEKLY]: { label: 'week' },
  [LOAN_PAY_FREQUENCY.FORTNIGHTLY]: { label: 'fortnight' },
  [LOAN_PAY_FREQUENCY.MONTHLY]: { label: 'month' }
};

function PersonalLoanCalculator({
  salesMode = false,
  ctaButtonLabel,
  applyUrl,
  applyCTATrackId,
  renderCalculatorIntro,
  loans1,
  isLefWaiver,
  isFinder
}) {
  const [repaymentAmount, setRepaymentAmount] = useState(0);
  const [totalInterestPaid, setTotalInterestPaid] = useState(0);
  const [totalAmountPaid, setTotalAmountPaid] = useState(0);
  const [repaymentFreq, setRepaymentFreq] = useState(INITIAL_REPAYMENT_FREQ);
  const [isVariable, setIsVariable] = useState(true);
  const [isSecured, setIsSecured] = useState(true);
  const [extraRepayments, setExtraRepayments] = useState(0);
  const [interestSaved, setInterestSaved] = useState(0);
  const [timeSavedYears, setTimeSavedYears] = useState(0);
  const [timeSavedMonths, setTimeSavedMonths] = useState(0);
  const [validationError, setValidationError] = useState('');

  return (
    <AnalyticsLocationProvider location="Calculate your repayments">
      <Box
        className="lfs-personal-loan-calculator"
        css={{
          backgroundColor: '#fff'
        }}
      >
        <StyledCalculatorFlex className="lfs-calculator-panel">
          <CalculatorFields
            loans1={loans1}
            isLefWaiver={isLefWaiver}
            salesMode={salesMode}
            setRepaymentAmount={setRepaymentAmount}
            setTotalInterestPaid={setTotalInterestPaid}
            setTotalAmountPaid={setTotalAmountPaid}
            repaymentFreq={repaymentFreq}
            setRepaymentFreq={setRepaymentFreq}
            isVariable={isVariable}
            setIsVariable={setIsVariable}
            extraRepayments={extraRepayments}
            setInterestSaved={setInterestSaved}
            setTimeSavedMonths={setTimeSavedMonths}
            setTimeSavedYears={setTimeSavedYears}
            setValidationError={setValidationError}
            validationError={validationError}
            isFinder={isFinder}
            isSecured={isSecured}
            setIsSecured={setIsSecured}
          />
          <CalculatorTotals
            loans1={loans1}
            isLefWaiver={isLefWaiver}
            salesMode={salesMode}
            repaymentPeriod={repaymentFreq}
            repaymentAmount={repaymentAmount}
            isVariable={isVariable}
            isSecured={isSecured}
            totalInterestPaid={totalInterestPaid}
            totalAmountPaid={totalAmountPaid}
            timeSavedMonths={timeSavedMonths}
            timeSavedYears={timeSavedYears}
            interestSaved={interestSaved}
            extraRepayments={extraRepayments}
            setExtraRepayments={setExtraRepayments}
            applyUrl={applyUrl}
            applyCTATrackId={applyCTATrackId || PL_APPLY_CTA_TRACK_ID}
            renderIntro={renderCalculatorIntro}
            validationError={validationError}
            ctaButtonLabel={ctaButtonLabel}
          />
        </StyledCalculatorFlex>
      </Box>
      <StyledDisclaimerBox className="lfs-calculator-disclaimer">
        <PLDisclaimerContent
          isLefWaiver={isLefWaiver}
          repaymentFreq={repaymentFreq}
          repaymentAmount={repaymentAmount}
          isVariable={isVariable}
          validationError={validationError}
          isSecured={isSecured}
        />
      </StyledDisclaimerBox>
    </AnalyticsLocationProvider>
  );
}

export function repaymentFreqLabel(repaymentFreq) {
  const result = REPAYMENT_FREQ_INFO[repaymentFreq].label;
  return result ? result : '(unknown)';
}

export default PersonalLoanCalculator;
